// selectors.ts
import { StatusType } from "@/redux/_status.types"
import { createSelector } from "@reduxjs/toolkit"
import { CustomEntityState, EntityWithId, Search, Sort } from "@/core/factory/types"
import { sortItems } from "@/core/utils/sort-items.utils"
import { isEqual } from "lodash"
import { Session } from "@/redux/_session.types"
import { sessionSelector } from "./session"
import makeInsensitive from "@/utils/make-insensitive.utils"

/**
 * Crée un selector combinant format personnalisé + tri générique + search
 * Utiliser dans les modules spécifiques (ex: risk) pour override `list`.
 */
export function createListSelector<T, C>(
  sliceSelector: (state: any) => CustomEntityState<T>,
  contextSelector: (state: any) => C,
  formatFn?: (item: T, _session: Session, context: C) => T
) {
  return createSelector([sliceSelector, sessionSelector, contextSelector], (slice, _session: Session, context: C) => {
    const list = slice.list ?? []

    let formatted = formatFn ? list.map((i) => formatFn(i, _session, context)) : list

    if (slice.search?.value) {
      const searchValue = slice.search.value ? makeInsensitive(slice.search.value) : ""
      if (searchValue.length) {
        formatted = formatted.filter((item) => {
          return slice.search!.columns.some((col) => {
            let value: any

            if (col.includes(".")) {
              // Handle one-level nested attribute (e.g. "attributes.description")
              const [parent, child] = col.split(".")
              value = (item as any)?.[parent]?.[child]
            } else {
              // Simple attribute (e.g. "name")
              value = (item as any)?.[col]
            }

            return typeof value === "string" && makeInsensitive(value).includes(searchValue)
          })
        })
      }
    }

    return sortItems(formatted, slice.sort)
  })
}

/**
 * Crée un ensemble de selectors génériques pour une entity Redux.
 * Le selector `list` applique le tri automatiquement.
 */
export function createEntitySelectors<T extends EntityWithId, C>(
  sliceName: string,
  contextSelector: (state: any) => C,
  formatFn?: (item: T, _session: Session, context: C) => T
) {
  const baseSelector = (state: any) => state[sliceName] as CustomEntityState<T>

  return {
    // Liste triée et formatée
    list: createListSelector<T, C>(baseSelector, contextSelector, formatFn),

    //List with only selected item
    listSelected: createSelector(
      [
        createListSelector<T, C>(baseSelector, contextSelector, formatFn),
        createSelector(baseSelector, (slice) => slice.selected as string[])
      ],
      (list, selectedIds) => {
        return list.filter((item) => selectedIds.includes(item.id))
      }
    ),

    //Detect if there is active item
    //L'entite est active quand sa cle de rattachement est renseignee : Establishment pour les
    //entites d'etablissement (risques, sites, populations), Account pour les familles de risques.
    isActive: createSelector(baseSelector, (slice) => {
      const active = slice.active as any
      return (active.EstablishmentId ?? active.AccountId ?? null) !== null
    }),

    //Detect if modification have been made
    isEdited: createSelector(baseSelector, (slice) => {
      return !isEqual(slice.active, slice.draft)
    }),

    // Return active entity formatted
    active: createSelector([baseSelector, sessionSelector, contextSelector], (slice, _session, context) =>
      formatFn ? formatFn(slice.active as T, _session, context) : (slice.active as T)
    ),

    // Return draft formatted
    draft: createSelector([baseSelector, sessionSelector, contextSelector], (slice, _session, context) =>
      formatFn ? formatFn(slice.draft as T, _session, context) : (slice.draft as T)
    ),

    isFullScreen: createSelector(baseSelector, (slice) => slice.isFullScreen as boolean),
    status: createSelector(baseSelector, (slice) => slice.status as StatusType),
    error: createSelector(baseSelector, (slice) => slice.error as string | null),
    selected: createSelector(baseSelector, (slice) => slice.selected as string[]),
    selectedDate: createSelector(baseSelector, (slice) => slice.selectedDate as Date | null),
    search: createSelector(baseSelector, (slice): Search => slice?.search ?? { value: null, columns: [] }),
    sort: createSelector(baseSelector, (slice): Sort => slice?.sort ?? { key: null, asc: true }),
    filter: createSelector(baseSelector, (slice) => slice.filter ?? null)
  }
}
